/**
 * VOC 数据预览表格组件
 * 使用 VTable 实现高性能数据展示（20000+ 条数据流畅渲染）
 * 自定义筛选下拉菜单：搜索 + 多选 + 勾选标识
 * 支持深色主题切换
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ListTable, themes } from '@visactor/vtable';
import type * as VTable from '@visactor/vtable';
import type { VocRecord } from '@/types/vocData';
import { useThemeStore } from '@/stores/themeStore';
import './VocDataTable.css';

// 筛选图标 SVG 生成
const filterSvg = (color: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16"><path d="M2.5 3h11L9.5 8.5V13L6.5 11.5V8.5L2.5 3z" fill="${color}"/></svg>`;

// 字段名称映射
const fieldTitleMap: Record<string, string> = {
  journey: '用户旅程',
  task: '任务',
  subTask: '子任务',
  bestPractice: '最佳体验项',
  issueTag: '情感',
  starRating: '星级',
};

interface VocDataTableProps {
  data: VocRecord[];
  loading?: boolean;
  activeFilters: Record<string, string[]>;
  onFilterChange?: (filteredData: VocRecord[]) => void;
  onActiveFiltersChange: (filters: Record<string, string[]>) => void;
}

// 根据筛选条件过滤数据
const applyFiltersToData = (
  data: VocRecord[],
  filters: Record<string, string[]>
): VocRecord[] => {
  let result = data;
  Object.entries(filters).forEach(([field, values]) => {
    if (values.length > 0) {
      result = result.filter((record) => {
        const val = record[field as keyof VocRecord];
        const strVal = typeof val === 'number' ? String(val) : (val as string);
        return values.includes(strVal);
      });
    }
  });
  return result;
};

// ===== 筛选下拉面板组件 =====
interface FilterDropdownPanelProps {
  title: string;
  options: string[];
  selected: string[];
  position: { x: number; y: number };
  isDark: boolean;
  onToggle: (value: string) => void;
  onSelectAll: () => void;
  onClose: () => void;
}

const FilterDropdownPanel: React.FC<FilterDropdownPanelProps> = ({
  title,
  options,
  selected,
  position,
  isDark,
  onToggle,
  onSelectAll,
  onClose,
}) => {
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Tooltip 状态
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  // 自动聚焦搜索框
  useEffect(() => {
    const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Escape 关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // 清理 hover timer
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // 鼠标进入选项：检测文本是否截断，300ms 后显示 tooltip
  const handleOptionMouseEnter = (option: string, e: React.MouseEvent) => {
    const textEl = (e.currentTarget as HTMLElement).querySelector('.filter-dropdown-text') as HTMLElement;
    if (textEl && textEl.scrollWidth > textEl.clientWidth) {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      hoverTimerRef.current = window.setTimeout(() => {
        // tooltip 定位到选项下方，左对齐面板
        let tipX = rect.left;
        let tipY = rect.bottom + 4;
        // 如果超出视口底部，改为显示在上方
        if (tipY + 40 > window.innerHeight) {
          tipY = rect.top - 40;
        }
        // 防止超出右侧视口
        if (tipX + 260 > window.innerWidth) {
          tipX = window.innerWidth - 268;
        }
        setTooltip({ text: option, x: tipX, y: tipY });
      }, 300);
    }
  };

  // 鼠标离开选项：清除 timer 和 tooltip
  const handleOptionMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setTooltip(null);
  };

  // 按搜索文本过滤选项
  const filteredOptions = searchText
    ? options.filter((opt) => opt.toLowerCase().includes(searchText.toLowerCase()))
    : options;

  const isAllSelected = selected.length === 0;

  return (
    <div className="filter-dropdown-overlay">
      <div className="filter-dropdown-backdrop" onClick={onClose} />
      <div
        className={`filter-dropdown-panel ${isDark ? 'dark-dropdown' : ''}`}
        style={{ left: position.x, top: position.y }}
      >
        {/* 标题 */}
        <div className="filter-dropdown-header">{title}</div>

        {/* 搜索框 */}
        <div className="filter-dropdown-search">
          <input
            ref={searchInputRef}
            type="text"
            className="filter-dropdown-search-input"
            placeholder="搜索..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* 选项列表 */}
        <div className="filter-dropdown-options">
          {/* 全部 */}
          <div
            className={`filter-dropdown-option ${isAllSelected ? 'selected' : ''}`}
            onClick={onSelectAll}
          >
            <span className="filter-dropdown-text">全部</span>
            <span className="filter-dropdown-check">{isAllSelected ? '✓' : ''}</span>
          </div>
          <div className="filter-dropdown-divider" />

          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const isSelected = selected.includes(option);
              return (
                <div
                  key={option}
                  className={`filter-dropdown-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => onToggle(option)}
                  onMouseEnter={(e) => handleOptionMouseEnter(option, e)}
                  onMouseLeave={handleOptionMouseLeave}
                >
                  <span className="filter-dropdown-text">{option}</span>
                  <span className="filter-dropdown-check">{isSelected ? '✓' : ''}</span>
                </div>
              );
            })
          ) : (
            <div className="filter-dropdown-empty">无匹配项</div>
          )}
        </div>
      </div>

      {/* 自定义 Tooltip */}
      {tooltip && (
        <div
          className={`filter-option-tooltip ${isDark ? 'dark-tooltip' : ''}`}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

// ===== 主组件 =====
const VocDataTable: React.FC<VocDataTableProps> = ({
  data,
  loading,
  activeFilters,
  onFilterChange,
  onActiveFiltersChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableInstanceRef = useRef<ListTable | null>(null);
  const { theme } = useThemeStore();

  // 筛选下拉面板状态
  const [filterDropdown, setFilterDropdown] = useState<{
    field: string;
    title: string;
    position: { x: number; y: number };
  } | null>(null);

  const isDark = theme === 'dark';

  // 计算某字段的可选值（交叉筛选）
  const getOptionsForField = useCallback(
    (field: string): string[] => {
      const filtersExcludingSelf = { ...activeFilters, [field]: [] };
      const dataForOptions = applyFiltersToData(data, filtersExcludingSelf);
      const uniqueSet = new Set<string>();
      dataForOptions.forEach((record) => {
        const value = record[field as keyof VocRecord];
        if (value !== undefined && value !== null && value !== '') {
          uniqueSet.add(String(value));
        }
      });
      return Array.from(uniqueSet).sort();
    },
    [data, activeFilters]
  );

  // 切换筛选选项
  const handleToggleFilterOption = useCallback(
    (value: string) => {
      if (!filterDropdown) return;
      const field = filterDropdown.field;
      const currentValues = activeFilters[field] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      onActiveFiltersChange({ ...activeFilters, [field]: newValues });
    },
    [filterDropdown, activeFilters, onActiveFiltersChange]
  );

  // 全选（清除筛选）
  const handleSelectAll = useCallback(() => {
    if (!filterDropdown) return;
    onActiveFiltersChange({ ...activeFilters, [filterDropdown.field]: [] });
  }, [filterDropdown, activeFilters, onActiveFiltersChange]);

  // 根据主题获取颜色配置
  const getThemeColors = () => {
    if (isDark) {
      return {
        basicHeaderBg: '#1F1F1F',
        basicCellBg: '#141414',
        basicText: '#B4B4B4',
        textHeaderBg: '#1A2332',
        textCellBg: '#0F1620',
        textText: '#93C5FD',
        categoryHeaderBg: '#1A2E1F',
        categoryCellBg: '#0F1A12',
        categoryText: '#86EFAC',
        tagHeaderBg: '#2E2319',
        tagCellBg: '#1A150E',
        tagText: '#FCD34D',
        headerText: '#FFFFFF',
        emptyText: '#8C8C8C',
      };
    } else {
      return {
        basicHeaderBg: '#fafafa',
        basicCellBg: '#ffffff',
        basicText: '#595959',
        textHeaderBg: '#e6f7ff',
        textCellBg: '#e6f7ff',
        textText: '#262626',
        categoryHeaderBg: '#f6ffed',
        categoryCellBg: '#f6ffed',
        categoryText: '#262626',
        tagHeaderBg: '#fff7e6',
        tagCellBg: '#fff7e6',
        tagText: '#262626',
        headerText: '#000',
        emptyText: '#999',
      };
    }
  };

  const colors = getThemeColors();

  // 生成筛选图标配置
  const getFilterIcon = (field: string) => ({
    type: 'svg' as const,
    svg: filterSvg(
      activeFilters[field]?.length > 0
        ? isDark ? '#60A5FA' : '#1677FF'
        : isDark ? '#666666' : '#BFBFBF'
    ),
    width: 14,
    height: 14,
    name: `filter_${field}`,
    positionType: 'absoluteRight' as any,
    cursor: 'pointer',
    marginRight: 4,
    interactive: true,
    tooltip: false as any,
  });

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return;

    // 计算筛选后的数据
    const filteredData = applyFiltersToData(data, activeFilters);

    // 列定义
    const columns: VTable.ColumnsDefine = [
      // ========== 基础组 - 固定列 ==========
      {
        field: 'vocId',
        title: 'VOC ID',
        width: 80,
        headerStyle: {
          bgColor: colors.basicHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: {
          bgColor: colors.basicCellBg,
          fontFamily: 'Monaco, Consolas, monospace',
          fontSize: 12,
          color: colors.basicText,
        },
      },
      {
        field: 'pn',
        title: 'PN',
        width: 120,
        headerStyle: {
          bgColor: colors.basicHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: {
          bgColor: colors.basicCellBg,
          fontFamily: 'Monaco, Consolas, monospace',
          fontSize: 12,
          color: colors.basicText,
        },
      },
      // ========== 文本组 - 蓝色标识 ==========
      {
        field: 'original',
        title: '原文',
        width: 300,
        headerStyle: {
          bgColor: colors.textHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: {
          bgColor: colors.textCellBg,
          padding: [10, 10, 10, 10],
          lineHeight: 20,
          fontSize: 13,
          color: colors.textText,
          autoWrapText: true,
        },
        mergeCell: true,
      },
      {
        field: 'slice',
        title: '切片',
        width: 150,
        headerStyle: {
          bgColor: colors.textHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: {
          bgColor: colors.textCellBg,
          padding: [10, 10, 10, 10],
          fontSize: 13,
          color: colors.textText,
          autoWrapText: true,
        },
      },
      // ========== 分类组 - 绿色标识（支持筛选）==========
      {
        field: 'journey',
        title: '用户旅程',
        width: 120,
        headerIcon: getFilterIcon('journey'),
        headerStyle: {
          bgColor: colors.categoryHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: {
          bgColor: colors.categoryCellBg,
          padding: [10, 10, 10, 10],
          fontSize: 13,
          color: colors.categoryText,
        },
      },
      {
        field: 'task',
        title: '任务',
        width: 120,
        headerIcon: getFilterIcon('task'),
        headerStyle: {
          bgColor: colors.categoryHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: {
          bgColor: colors.categoryCellBg,
          padding: [10, 10, 10, 10],
          fontSize: 13,
          color: colors.categoryText,
        },
      },
      {
        field: 'subTask',
        title: '子任务',
        width: 120,
        headerIcon: getFilterIcon('subTask'),
        headerStyle: {
          bgColor: colors.categoryHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: {
          bgColor: colors.categoryCellBg,
          padding: [10, 10, 10, 10],
          fontSize: 13,
          color: colors.categoryText,
        },
      },
      // ========== 洞察组 - 橙色标识 ==========
      {
        field: 'bestPractice',
        title: '最佳体验项',
        width: 200,
        headerIcon: getFilterIcon('bestPractice'),
        headerStyle: {
          bgColor: colors.tagHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: {
          bgColor: colors.tagCellBg,
          padding: [10, 10, 10, 10],
          fontSize: 13,
          color: colors.tagText,
          autoWrapText: true,
        },
      },
      {
        field: 'issueTag',
        title: '情感',
        width: 150,
        headerIcon: getFilterIcon('issueTag'),
        headerStyle: {
          bgColor: colors.tagHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: {
          bgColor: colors.tagCellBg,
          padding: [10, 10, 10, 10],
          fontSize: 13,
          color: colors.tagText,
        },
      },
      {
        field: 'starRating',
        title: '星级',
        width: 60,
        headerIcon: getFilterIcon('starRating'),
        headerStyle: {
          bgColor: colors.tagHeaderBg,
          color: colors.headerText,
          fontWeight: 'bold',
          fontSize: 13,
        },
        style: (args: any) => {
          const rating = args.dataValue as number;
          const bgMap: Record<number, string> = isDark
            ? { 1: '#3B1515', 2: '#3B2E15', 3: '#2A2A2A', 4: '#153B1F', 5: '#0F3B1A' }
            : { 1: '#FEE2E2', 2: '#FEF3C7', 3: '#F3F4F6', 4: '#DCFCE7', 5: '#BBF7D0' };
          const colorMap: Record<number, string> = isDark
            ? { 1: '#FCA5A5', 2: '#FCD34D', 3: '#9CA3AF', 4: '#86EFAC', 5: '#4ADE80' }
            : { 1: '#DC2626', 2: '#D97706', 3: '#6B7280', 4: '#16A34A', 5: '#15803D' };
          return {
            bgColor: bgMap[rating] ?? colors.tagCellBg,
            color: colorMap[rating] ?? colors.tagText,
            textAlign: 'center',
            fontSize: 14,
            fontWeight: 'bold',
            padding: [10, 10, 10, 10],
          };
        },
      },
    ];

    // 创建 VTable 实例
    const tableInstance = new ListTable({
      container: containerRef.current,
      records: filteredData,
      columns,

      // ===== 布局配置 =====
      frozenColCount: 2,
      defaultRowHeight: 64,
      defaultHeaderRowHeight: 40,
      widthMode: 'standard',

      // ===== 空数据提示 =====
      emptyTip: {
        text: '暂无数据',
      } as any,

      // ===== 悬浮提示 =====
      tooltip: {
        renderMode: 'html',
        isShowOverflowTextTooltip: true,
      },

      // ===== 选择配置 =====
      select: {
        disableSelect: false,
        highlightMode: 'cell',
        blankAreaClickDeselect: true,
      },

      // ===== 键盘操作 =====
      keyboardOptions: {
        copySelected: true,
        moveSelectedCellOnArrowKeys: true,
        selectAllOnCtrlA: true,
      },

      // ===== 列宽调整 =====
      resize: {
        columnResizeMode: 'header',
      },

      // ===== 拖拽移动列 =====
      dragHeaderMode: 'column',

      // ===== 右键菜单（冻结列）=====
      menu: {
        renderMode: 'html',
        contextMenuItems: [
          { text: '冻结到此列', menuKey: 'freeze_to_col' },
          { text: '取消冻结', menuKey: 'freeze_0' },
        ],
      },

      // ===== 主题 =====
      theme: isDark
        ? {
            ...themes.DARK,
            selectionStyle: {
              cellBorderColor: '#60A5FA',
              cellBorderLineWidth: 2,
            },
          }
        : {
            ...themes.BRIGHT,
            defaultStyle: {
              ...themes.BRIGHT.defaultStyle,
              borderColor: '#D4DAE3',
              borderLineWidth: 1,
            },
            headerStyle: {
              ...themes.BRIGHT.headerStyle,
              borderColor: '#C5CDD8',
              borderLineWidth: 1,
            },
            bodyStyle: {
              ...themes.BRIGHT.bodyStyle,
              borderColor: '#D4DAE3',
              borderLineWidth: 1,
            },
            frameStyle: {
              ...themes.BRIGHT.frameStyle,
              borderColor: '#D4DAE3',
              borderLineWidth: 1,
            },
            selectionStyle: {
              cellBorderColor: '#1677FF',
              cellBorderLineWidth: 2,
            },
          },
    });

    // 通知父组件筛选后的数据
    onFilterChange?.(filteredData);

    // 右键菜单事件（冻结列）
    tableInstance.on('dropdown_menu_click', (args: any) => {
      const { menuKey, col } = args;
      if (menuKey === 'freeze_to_col') {
        (tableInstance as any).frozenColCount = col + 1;
        tableInstance.renderWithRecreateCells();
      } else if (menuKey === 'freeze_0') {
        (tableInstance as any).frozenColCount = 0;
        tableInstance.renderWithRecreateCells();
      }
    });

    // 筛选图标点击 → 打开自定义下拉面板
    tableInstance.on('icon_click', (args: any) => {
      const { name, event: domEvent } = args;
      if (typeof name === 'string' && name.startsWith('filter_')) {
        const field = name.replace('filter_', '');
        const e = domEvent as MouseEvent;
        const clickX = e?.clientX ?? 0;
        const clickY = e?.clientY ?? 0;

        // 计算面板位置，确保不超出视口
        const panelWidth = 220;
        const panelHeight = 320;
        let x = clickX - panelWidth + 20;
        let y = clickY + 8;
        if (x + panelWidth > window.innerWidth - 8) x = window.innerWidth - panelWidth - 8;
        if (y + panelHeight > window.innerHeight - 8) y = clickY - panelHeight - 8;
        if (x < 8) x = 8;
        if (y < 8) y = 8;

        setFilterDropdown({
          field,
          title: fieldTitleMap[field] || field,
          position: { x, y },
        });
      }
    });

    tableInstanceRef.current = tableInstance;

    return () => {
      if (tableInstanceRef.current) {
        tableInstanceRef.current.release();
        tableInstanceRef.current = null;
      }
    };
  }, [data, theme, activeFilters]);

  // Ctrl+C 复制为 TSV 格式
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && tableInstanceRef.current) {
        const selectedRanges = tableInstanceRef.current.getSelectedCellRanges();
        if (selectedRanges && selectedRanges.length > 0) {
          e.preventDefault();

          const selectedData: string[][] = [];
          selectedRanges.forEach((range: any) => {
            for (let row = range.start.row; row <= range.end.row; row++) {
              const rowData: string[] = [];
              for (let col = range.start.col; col <= range.end.col; col++) {
                const cellValue = tableInstanceRef.current!.getCellValue(col, row);
                rowData.push(cellValue?.toString() || '');
              }
              selectedData.push(rowData);
            }
          });

          const tsvData = selectedData.map((row) => row.join('\t')).join('\n');
          navigator.clipboard.writeText(tsvData);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // 下拉面板的选项和已选值
  const dropdownOptions = filterDropdown ? getOptionsForField(filterDropdown.field) : [];
  const dropdownSelected = filterDropdown ? activeFilters[filterDropdown.field] || [] : [];

  return (
    <div className="voc-data-table-wrapper">
      {loading && (
        <div className="table-loading">
          <div className="loading-spinner" />
          <span>加载中...</span>
        </div>
      )}
      <div ref={containerRef} className="voc-data-table-container" />
      {filterDropdown &&
        createPortal(
          <FilterDropdownPanel
            title={filterDropdown.title}
            options={dropdownOptions}
            selected={dropdownSelected}
            position={filterDropdown.position}
            isDark={isDark}
            onToggle={handleToggleFilterOption}
            onSelectAll={handleSelectAll}
            onClose={() => setFilterDropdown(null)}
          />,
          document.body
        )}
    </div>
  );
};

export default VocDataTable;
